// @flow

import * as React from 'react';

import type { ProductError } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Base, Button, UnorderedList } from '@gandi/ui';

import { useFeatureFlags } from 'src/hooks/useFeatureFlag';

import ErrorItem from './ErrorItem';

type Props = {
  handleRemoveAction: (SyntheticEvent<HTMLButtonElement>) => mixed,
  errors?: Array<ProductError>,
};

function RemoveButton({
  handleRemoveAction,
}: {
  handleRemoveAction: Props['handleRemoveAction'],
}) {
  const { __ } = useTranslator();

  return (
    <Button kind="link" type="button" onClick={handleRemoveAction}>
      {__('Remove product')}
    </Button>
  );
}

export default function ErrorRow({
  handleRemoveAction,
  errors,
}: Props): React.MixedElement {
  const { FF_CART_OVERVIEW_NDS } = useFeatureFlags();
  const { __ } = useTranslator();

  const messageItems = React.useMemo(() => {
    if (!errors || errors.length === 0)
      return (
        <ErrorItem
          key="errors"
          cta={<RemoveButton handleRemoveAction={handleRemoveAction} />}
        >
          {__(
            'Sorry, something unexpected happened. Retry later or remove this product to continue.',
          )}
        </ErrorItem>
      );

    return FF_CART_OVERVIEW_NDS ? (
      <ErrorItem
        cta={<RemoveButton handleRemoveAction={handleRemoveAction} />}
        options={errors.map(err => err.description)}
        title={__('Error:')}
      />
    ) : (
      // Legacy style
      errors.map(err => (
        <ErrorItem key={`${err.description}-${err.name}`}>
          {err.description}
        </ErrorItem>
      ))
    );
  }, [FF_CART_OVERVIEW_NDS, __, errors, handleRemoveAction]);

  if (FF_CART_OVERVIEW_NDS) {
    return (
      <Base flex flexCol gap="spacing-1x">
        {messageItems}
      </Base>
    );
  }

  // Legacy style
  return (
    <Base flex flexCol gap="xsmall">
      <UnorderedList gap="xxsmall" icon="dangerWire" iconTheme="danger">
        {
          // $FlowFixMe[incompatible-type]
          messageItems
        }
      </UnorderedList>
      <Button small kind="outline" onClick={handleRemoveAction} flexExpandRight>
        {__('Remove product')}
      </Button>
    </Base>
  );
}
